import React, {ReactNode, useContext, useEffect} from 'react'
import {ButtonProps, DropdownInput, Icon, Text} from "atmosfair-ui";
import {
    ACTIVITY_TYPE,
    AllActivityTypes,
    AllActivityTypesArray,
    TRAVEL_OPTION_TYPE,
    TravelOptionTypeToActivityMappingObject
} from "../../../../types-enums/activity";
import {PATH_SCREEN_STRING, PATH_STRINGS} from "../../../../routes/route-props";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {
    convertActivityTypesToDropdownFormat,
    convertAddActivitiesToJourneyToDropdownFormat
} from "../../../../utils/converter/dropdown";
import JourneyDayCardItem, {ICON_SIZE_DND_ICON} from "../journey-day-card-item/JourneyDayCardItem";
import HotelLocation from "../../../../molecules/activity/string-display/hotel-location/HotelLocation";
import {isActivityCarBusState, isActivityFlightState, isActivityHotelState} from "../../../../utils/check/type-guards";
import {theme} from "../../../../styles/theme";
import {groupActivitiesByTravelOptionType, JourneyDayCardContext} from "../../common";
import clsx from "clsx";
import {getFirstActivityByActivityGroupId} from "../../../../utils/getter/activities";
import {isActivityWithNextDayInformation, recalculateActivityOnDifferentDate} from "../../../../utils/check/activities";
import {generateProfessionalFlightRouteWithoutCo2KgEStrings} from "../../../../utils/string/flight";
import {generateCustomerHotelLocationString} from "../../../../utils/string/hotel";
import {generateProfessionalCarBusInformationString} from "../../../../utils/string/car-bus";
import {
    generateDayString,
    generateTotalCo2KgEOrFailedString,
    generateTranslatedActivityTypeString,
    WhereToDisplayActivityString
} from "../../../../utils/string/common";
import useRecalculateActivityOnDifferentDate from "../../../../hooks/use-recalculate-activity-on-different-date";
import {toast, TypeOptions} from "react-toastify";
import ToastWithTextOnly from "../../../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
import {addActivityToJourney} from "../../../../redux/reducer/journey";
import {useAppDispatch} from "../../../../redux/reducer/store";
import cloneDeep from "lodash/cloneDeep";
import {hasExactDates} from "../../../../utils/getter/journey";
import {COMMON_COLOR, getDaysOfActivitiesGroup} from "../common";
import "./styles.css"

export interface JourneyOverviewCardListItemsProps {
    travelOptionType: TRAVEL_OPTION_TYPE,
}

export interface AddActivityFromTravelComponentsTableDropdownProps {
    label: string,
    activity: AllActivityTypes
}

export const MAX_WIDTH_PERCENTAGE_DND_ITEM = 50
const ADD_ACTIVITIES_ICON_SIZE = "medium"

const JourneyDayCardItems = ({
                                 travelOptionType,
                             }: JourneyOverviewCardListItemsProps) => {
    const {
        allActivities,
        activitiesAtDay,
        day,
        journeyDurationInDays,
        hoveredId,
        isRecalculatingActivityEmissions,
        draggingActivity,
        travelComponentActivities,
        journeyDuration,
        journeyPersonCount,
    } = useContext(JourneyDayCardContext)
    const dispatch = useAppDispatch()
    const activitiesGroupedByTravelOptionType = groupActivitiesByTravelOptionType(activitiesAtDay)
    const navigate = useNavigate()
    const {i18n, t} = useTranslation()
    const {
        isLoading,
        updatedActivities,
        recalculateActivityEmissionWitNewGivenDay
    } = useRecalculateActivityOnDifferentDate({
        journeyDuration,
        renderToast: (message, type) => _renderToast(message, type),
    })

    useEffect(() => {
        if (updatedActivities) _addActivityToJourney(updatedActivities[0])
    }, [updatedActivities])

    const _addActivityToJourney = (activity: AllActivityTypes) => {
        if (typeof day !== "undefined") {
            dispatch(addActivityToJourney({
                activity: activity,
                travelComponentActivities: allActivities,
                day
            }))
            _renderToast(t("TOASTS.SUCCESS.ACTIVITY_ADDED_TO_JOURNEY", {
                activity: generateTranslatedActivityTypeString(activity.common.activityType, i18n),
                interpolation: {escapeValue: false}
            }), "success")
        }
    }

    const _renderToast = (message: string, type: TypeOptions) => toast(<ToastWithTextOnly
            message={message}/>,
        {progress: undefined, type}
    )

    const _addActivityWithNextDayInformation = (activity: AllActivityTypes, activitiesWithNextDayInformation: AllActivityTypesArray) => {
        const {common: {activityType}} = activity
        if (isActivityWithNextDayInformation(activityType)) activitiesWithNextDayInformation.push(activity)
    }

    const _renderPossibleNextDayInformation = () => {
        const activitiesWithNextDayInformation: AllActivityTypesArray = []
        if (typeof day !== 'undefined') {
            if (day > 1) {
                const previousDay = day - 1
                allActivities
                    .filter(activity => activity.common.day === previousDay && activity.common.groupId === null)
                    .forEach(activity => _addActivityWithNextDayInformation(activity, activitiesWithNextDayInformation))
                const activityGroupIdsOnPreviousDay = allActivities
                    .filter(activity => activity.common.day === previousDay)
                    .map(activity => activity.common.groupId)
                    .filter(Boolean)
                const activityGroupIdsOnCurrentDay = allActivities
                    .filter(activity => activity.common.day === day)
                    .map(activity => activity.common.groupId)
                    .filter(Boolean)

                activityGroupIdsOnPreviousDay.forEach(activityGroupIdOnPreviousDay => {
                    if (!activityGroupIdsOnCurrentDay.includes(activityGroupIdOnPreviousDay) && activityGroupIdOnPreviousDay !== null) {
                        const activity = getFirstActivityByActivityGroupId(allActivities, activityGroupIdOnPreviousDay)
                        if (activity) _addActivityWithNextDayInformation(activity, activitiesWithNextDayInformation)
                    }
                })
            }
        }

        return activitiesWithNextDayInformation
    }

    const _setDisableAddActivitiesFromTravelComponentsTable = (activities: AllActivityTypesArray) => {
        if (activities === null || typeof activities === 'undefined' || activities.length === 0) return true
        // if (typeof day !== "undefined" && typeof journeyDurationInDays !== "undefined") return activities.every(activity => activityDurationExceedsJourneyDurationFromDay(activity, day, journeyDurationInDays))
        return false
    }

    const _renderActivityAddButton = (travelOptionType: TRAVEL_OPTION_TYPE) => {
        if (day === journeyDurationInDays && travelOptionType === TRAVEL_OPTION_TYPE.accommodation) return <Text
            classes={'journey-day-card-item-travel-option-selection-wrapper-padding'}
            color={COMMON_COLOR}>HINTS.JOURNEY.ADD_HOTEL_ACTIVITY_LAST_JOURNEY_DAY</Text>
        const commonProps = {
            width: 28,
            iconOnlyColor: COMMON_COLOR,
            iconOnlySize: "small" as ButtonProps["size"],
            iconOnly: true,
            useSelection: false,
        }
        let activitiesFromTable: (AddActivityFromTravelComponentsTableDropdownProps | null)[] = []

        if (travelComponentActivities) {
            activitiesFromTable = travelComponentActivities.map(travelComponentActivity => {
                const {common: {activityType, travelOptionType: activityTravelOptionType}} = travelComponentActivity
                if (travelOptionType === activityTravelOptionType) {
                    let activityString = ''
                    switch (activityType) {
                        case ACTIVITY_TYPE.flight:
                            if (isActivityFlightState(travelComponentActivity)) activityString = `${generateProfessionalFlightRouteWithoutCo2KgEStrings(travelComponentActivity.legs, i18n).flightRouteString}`
                            break;
                        case ACTIVITY_TYPE.hotel:
                            if (isActivityHotelState(travelComponentActivity)) {
                                activityString = generateCustomerHotelLocationString(
                                    travelComponentActivity,
                                    i18n,
                                    true,
                                    false,
                                    WhereToDisplayActivityString.dropdownJourney
                                ).locationString
                            }
                            break;
                        case ACTIVITY_TYPE.car_bus:
                            if (isActivityCarBusState(travelComponentActivity)) {
                                const duration = travelComponentActivity.countDays === null ? 1 : travelComponentActivity.countDays
                                activityString = `${generateProfessionalCarBusInformationString(travelComponentActivity, i18n, true).carBusString}, ${generateDayString(duration, i18n)}`
                            }
                            break;
                        default:
                            activityType satisfies never
                    }
                    return {
                        label: `${activityString} (${generateTotalCo2KgEOrFailedString(travelComponentActivity, i18n)})`,
                        activity: travelComponentActivity
                    }
                } else {
                    return null
                }
            }).filter(Boolean)
        }

        return <div className={"journey-day-card-items-add-activity-buttons-wrapper"}>
            <DropdownInput
                {...commonProps}
                label={"TOOLTIP.JOURNEY.CALCULATE_NEW_ACTIVITY"}
                options={convertActivityTypesToDropdownFormat(TravelOptionTypeToActivityMappingObject[travelOptionType], i18n)}
                iconOnlyIcon={"calculator"}
                iconOnlySize={ADD_ACTIVITIES_ICON_SIZE}
                setSelection={selection => {
                    if (selection) {
                        const activityType = selection[0].raw.activityType
                        navigate(`${PATH_STRINGS.addActivityToJourney}/${PATH_SCREEN_STRING.journey}/${activityType}/${day}`)
                    }
                }}/>
            {typeof journeyDurationInDays !== 'undefined' && typeof day !== 'undefined' && <DropdownInput
                {...commonProps}
                readOnly={_setDisableAddActivitiesFromTravelComponentsTable(
                    groupActivitiesByTravelOptionType(activitiesFromTable.filter(Boolean).map(activity => activity!!.activity))[travelOptionType]
                )}
                iconOnlyListBoxWidth={600}
                iconOnlySize={ADD_ACTIVITIES_ICON_SIZE}
                iconOnlyIcon={"shapes"}
                label={"TOOLTIP.JOURNEY.ADD_ACTIVITY_FROM_TRAVEL_COMPONENT_TABLE"}
                options={activitiesFromTable ? convertAddActivitiesToJourneyToDropdownFormat(activitiesFromTable, journeyDurationInDays, journeyPersonCount, day, i18n) : []}
                setSelection={selection => {
                    if (selection && typeof day !== 'undefined') {
                        const activityToMoveToJourney = cloneDeep(selection[0]?.raw?.activity)
                        if (hasExactDates(journeyDuration) && activityToMoveToJourney) {
                            const {common: {activityType}} = activityToMoveToJourney
                            if (recalculateActivityOnDifferentDate(activityType)) {
                                recalculateActivityEmissionWitNewGivenDay(activityToMoveToJourney, day)
                            } else {
                                _addActivityToJourney(activityToMoveToJourney)
                            }
                        } else {
                            _addActivityToJourney(activityToMoveToJourney)
                        }
                    }
                }}/>
            }
        </div>
    }

    const _activityWithNextDayInformation = () => _renderPossibleNextDayInformation().map((activity, index) => {
        const {common: {activityType, travelOptionType: activityTravelOptionType}} = activity
        if (activityTravelOptionType === travelOptionType) {
            if (isActivityWithNextDayInformation(activityType)) {
                if (isActivityHotelState(activity)) return <div
                    key={index}
                    style={{marginBottom: theme.spacer.small}}
                    className={clsx('journey-day-card-item-activity-button-wrapper', 'journey-day-card-item-travel-option-selection-wrapper-padding')}>
                    <Icon
                        iconStyle={{marginRight: "12px", paddingTop: "4px"}}
                        tooltip={"GENERAL.CHECKOUT"}
                        icon={"personWalkingLuggage"}
                        color={theme.colors.lightGreyDarker}
                        size={ICON_SIZE_DND_ICON}/>
                    <HotelLocation
                        iconSize={ICON_SIZE_DND_ICON}
                        iconColor={theme.colors.lightGreyDarker}
                        hideAllIconHints={true}
                        whereToDisplay={WhereToDisplayActivityString.journey}
                        truncatedWordsListMaxWidthPercentage={MAX_WIDTH_PERCENTAGE_DND_ITEM}
                        textColor={"lightGreyDarker"}
                        hotel={activity}/>
                </div>
            }
        }
    })

    const _renderActivities = () => {
        if (travelOptionType in activitiesGroupedByTravelOptionType) {
            const activityUiIdsForTravelOption = activitiesGroupedByTravelOptionType[travelOptionType];
            let elementsToRender: ReactNode[] = []
            if (activityUiIdsForTravelOption && activityUiIdsForTravelOption.length > 0) {
                activitiesGroupedByTravelOptionType[travelOptionType]?.forEach((activity, index) => {
                    if (typeof day !== 'undefined') {
                        if (activity) {
                            const {common: {id, groupId}} = activity
                            let highlightOnHover = false
                            if (hoveredId !== null && groupId !== null) highlightOnHover = hoveredId === groupId
                            const JourneyCardItem = <JourneyDayCardItem
                                daysOfGroup={getDaysOfActivitiesGroup(allActivities, groupId)}
                                highlight={highlightOnHover}
                                activity={activity}
                                loading={isRecalculatingActivityEmissions || isLoading}
                                key={index}/>

                            if (draggingActivity === null) {
                                elementsToRender.push(JourneyCardItem)
                            } else {
                                if (id !== draggingActivity?.common.id) elementsToRender.push(JourneyCardItem)
                            }
                        }
                    }
                })
            }
            return <>
                {_renderActivityAddButton(travelOptionType)}
                {_activityWithNextDayInformation()}
                {elementsToRender}
            </>

        }
        return <>
            {_renderActivityAddButton(travelOptionType)}
            {_activityWithNextDayInformation()}
        </>
    }

    return <>
        {_renderActivities()}
    </>
}

export default JourneyDayCardItems